import React, { Component } from 'react';
import { Provider } from 'react-redux';
import { routes } from 'constants/config';

import editorStore from 'stores/editorStore';

import request from 'utils/request_ajax';

import ConversationScenarioEditor from './ConversationScenarioEditor';
import ScenarioEditor from './ScenarioEditor';
import ErrorModal from './ErrorModal';

// Handle users and groups with a composite id
const identifier = contributor => `${contributor.id}.${contributor.type}`;

class OnDemandPracticeEditor extends Component {
  constructor(props) {
    super(props);
    this.store = editorStore({
      editor: { currentUserId: props.currentUserId },
      meta: {},
    });
  }

  state = {
    loading: true,
    scenario: null,
    saved: false,
    savingError: false,
    potentialFeedbackContributors: [],
  };

  loadScenario = () => {
    this.setState({
      loading: true,
    });
    request
      .get(routes.ON_DEMAND_PRACTICE.CREATOR.EDIT(this.props.scenarioId))
      .then(scenario => {
        let potentialFeedbackContributors = [];
        if (scenario && scenario.feedbackContributors) {
          potentialFeedbackContributors = scenario.feedbackContributors || [];
          delete scenario.feedbackContributors;
        }
        let feedbackCriteria = [];
        if (scenario && scenario.feedbackCriteria) {
          feedbackCriteria = scenario.feedbackCriteria || [];
        }

        this.setState({
          loading: false,
          scenario,
          potentialFeedbackContributors,
          feedbackCriteria,
        });
      });
  };

  componentDidMount() {
    this.loadScenario();
  }

  setSavedState() {
    this.setState(
      {
        saved: true,
      },
      () => {
        setTimeout(() => {
          this.setState({
            saved: false,
          });
        }, 2000);
      },
    );
  }

  saveScenario = () => {
    const { scenario } = this.state;
    const scenarioData = {
      scenario: {
        prompt: scenario.prompt,
        title: scenario.title,
        description: scenario.description,
      },
    };
    if (scenario.scenarioType === 'conversation') {
      scenarioData.scenario.conversation = scenario.conversation;
    }
    request
      .put(
        routes.ON_DEMAND_PRACTICE.CREATOR.UPDATE(this.props.scenarioId),
        scenarioData,
      )
      .then(data => {
        if (data.success) {
          this.setSavedState();
        } else {
          this.setState({ savingError: true });
        }
      })
      .fail(() => {
        this.setState({ savingError: true });
      });
  };

  updatePrompt = prompt => {
    this.setState(
      state => ({
        scenario: {
          ...state.scenario,
          prompt,
        },
      }),
      () => {
        this.saveScenario();
      },
    );
  };

  updateTitle = title => {
    this.setState(
      state => ({
        scenario: {
          ...state.scenario,
          title,
        },
      }),
      () => {
        this.saveScenario();
      },
    );
  };

  updateDescription = description => {
    this.setState(
      state => ({
        scenario: {
          ...state.scenario,
          description,
        },
      }),
      () => {
        this.saveScenario();
      },
    );
  };

  filterSuggestions = potentialFeedbackContributors => {
    const selected = this.state.potentialFeedbackContributors.map(identifier);

    return potentialFeedbackContributors.filter(
      suggestion => !selected.includes(identifier(suggestion)),
    );
  };

  onSuggestionSelected = potentialFeedbackContributor => {
    this.setState(({ potentialFeedbackContributors }) => ({
      potentialFeedbackContributors: [
        ...potentialFeedbackContributors,
        potentialFeedbackContributor,
      ],
    }));

    request
      .post(
        routes.ON_DEMAND_PRACTICE.CREATOR.FEEDBACK_CONTRIBUTOR_OPTIONS.INDEX(
          this.props.scenarioId,
        ),
        { ...potentialFeedbackContributor },
      )
      .then((response, status, { status: statusCode }) => {
        if (statusCode === 204) {
          this.setSavedState();
        } else {
          this.setState({ savingError: true });
        }
      })
      .catch(e => {
        this.setState({ savingError: true });
      });
  };

  onSuggestionRemoved = potentialFeedbackContributor => () => {
    this.setState(({ potentialFeedbackContributors }) => ({
      potentialFeedbackContributors: potentialFeedbackContributors.filter(
        selectedPotentialFeedbackContributor =>
          identifier(selectedPotentialFeedbackContributor) !==
          identifier(potentialFeedbackContributor),
      ),
    }));

    request
      .delete(
        `${routes.ON_DEMAND_PRACTICE.CREATOR.FEEDBACK_CONTRIBUTOR_OPTIONS.INDEX(
          this.props.scenarioId,
        )}?id=${encodeURI(potentialFeedbackContributor.id)}&type=${encodeURI(
          potentialFeedbackContributor.type,
        )}`,
      )
      .then((response, status, { status: statusCode }) => {
        if (statusCode === 204) {
          this.setSavedState();
        } else {
          this.setState({ savingError: true });
        }
      })
      .catch(e => {
        this.setState({ savingError: true });
      });
  };

  updateFeedbackCriteria = (feedbackCriteria = []) => {
    this.setState({ feedbackCriteria });

    request
      .post(
        routes.ON_DEMAND_PRACTICE.CREATOR.FEEDBACK_CRITERIA.INDEX(
          this.props.scenarioId,
        ),
        {
          criteria: feedbackCriteria.map(({ id }) => id),
        },
      )
      .then((response, status, { status: statusCode }) => {
        if (statusCode === 200) {
          this.setSavedState();
        } else {
          this.setState({ savingError: true });
        }
      })
      .catch(e => {
        this.setState({ savingError: true });
      });
  };

  renderEditor = () => {
    const {
      scenario,
      loading,
      saved,
      potentialFeedbackContributors,
      feedbackCriteria,
    } = this.state;
    switch (this.state.scenario.scenarioType) {
      case 'conversation':
        return (
          <ConversationScenarioEditor
            scenarioId={this.props.scenarioId}
            updatePrompt={this.updatePrompt}
            updateDescription={this.updateDescription}
            updateTitle={this.updateTitle}
            updateConversation={this.updateConversation}
            scenario={scenario}
            loading={loading}
            saved={saved}
          />
        );
      default:
        return (
          <ScenarioEditor
            scenarioId={this.props.scenarioId}
            features={this.props.features}
            browserSupport={this.props.browserSupport}
            updatePrompt={this.updatePrompt}
            updateDescription={this.updateDescription}
            updateTitle={this.updateTitle}
            updateFeedbackCriteria={this.updateFeedbackCriteria}
            identifier={identifier}
            filterSuggestions={this.filterSuggestions}
            onSuggestionSelected={this.onSuggestionSelected}
            onSuggestionRemoved={this.onSuggestionRemoved}
            scenario={scenario}
            loading={loading}
            saved={saved}
            feedbackCriteria={feedbackCriteria}
            potentialFeedbackContributors={potentialFeedbackContributors}
            currentUserId={this.props.currentUserId}
            ziggeoApiKey={this.props.ziggeoApiKey}
          />
        );
    }
  };

  render() {
    const { savingError, scenario } = this.state;
    return (
      <Provider store={this.store}>
        <>
          {scenario && this.renderEditor()}
          {savingError && <ErrorModal />}
        </>
      </Provider>
    );
  }
}

export default props => <OnDemandPracticeEditor {...props} />;
